"use client";

import { useState, useEffect, useContext } from "react";
import {
  Box,
  TextField,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  TablePagination,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ErpContext } from "../../context/ErpContext.js";
import ModalCrearActualizarEntidad from "./ModalCrearActualizarEntidad";

const TablaEntidadProveedor = () => {
  const theme = useTheme();
  const { getEntidadFilterTipoEntidad ,isNavbarCollapsed,
  } = useContext(ErpContext);

  // Estados
  const [proveedores, setProveedores] = useState([]);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Obtener proveedores
  useEffect(() => {
    fetchProveedores();
  }, []);

  const fetchProveedores = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEntidadFilterTipoEntidad(2); // Tipo 2 = Proveedores
      setProveedores(data || []);
      setFilteredProveedores(data || []);
    } catch (err) {
      console.error("Error al cargar proveedores:", err);
      setError("No se pudieron cargar los proveedores. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar proveedores según la búsqueda
  useEffect(() => {
    if (!searchText) {
      setFilteredProveedores(proveedores);
      return;
    }

    const filtered = proveedores.filter(
      (prov) =>
        prov.descripcion?.toLowerCase().includes(searchText.toLowerCase()) ||
        prov.apellido?.toLowerCase().includes(searchText.toLowerCase()) ||
        prov.dniCuitCuil?.toLowerCase().includes(searchText.toLowerCase()) ||
        prov.email?.toLowerCase().includes(searchText.toLowerCase())
    );

    setFilteredProveedores(filtered);
    setPage(0); // Resetear a primera página cuando se filtra
  }, [searchText, proveedores]);

  // Abrir modal para EDITAR proveedor
  const handleOpenEditModal = (proveedor) => {
    setSelectedProveedor(proveedor); // Cargar datos en el modal
    setOpenModal(true);
  };

  // Abrir modal para CREAR proveedor
  const handleOpenCreateModal = () => {
    setSelectedProveedor({
      id: null,
      descripcion: "",
      apellido: "",
      dniCuitCuil: "",
      telefono: "",
      email: "",
      direccion: "",
      localidad: "",
      provincia: "",
      idTipoEntidad: 2, // Tipo entidad = Proveedor
    });
    setOpenModal(true);
  };

  // Cerrar modal y actualizar lista si se guardaron cambios
  const handleCloseModal = async (refresh = false) => {
    setOpenModal(false);
    if (refresh) {
      await fetchProveedores();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular proveedores para la página actual
  const paginatedProveedores = filteredProveedores.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const tableStyles = {
    "&::-webkit-scrollbar": {
      width: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "10px",
    },
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflow: "hidden" }}>
      {/* Encabezado */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <BusinessIcon
              sx={{
                fontSize: 28,
                color: "rgb(244, 147, 14)",
                mr: 1.5,
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              Gestión de Proveedores
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
            }}
          >
            {/* Buscador */}
            <TextField
              placeholder="Buscar por nombre, apellido, CUIT o email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                maxWidth: { sm: "60%", md: "40%" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Botones de acción */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchProveedores}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  minWidth: 100,
                }}
              >
                Actualizar
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpenCreateModal}
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: 2,
                  minWidth: 140,
                  backgroundColor: "#5b5b5f",
                  "&:hover": {
                    backgroundColor: alpha("#5b5b5f", 0.8),
                  },
                }}
              >
                Nuevo Proveedor
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerta de éxito */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setSuccess(false)}
        >
          Operación completada con éxito
        </Alert>
      </Fade>

      {/* Alerta de error */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Tabla de proveedores */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          height: "auto",
        }}
      >
        <Box sx={{ position: "relative" }}>
          {/* Overlay de carga */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
              ...tableStyles,
            }}
          >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ height: "45px" }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "5%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Nombre
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Apellido
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    DNI / CUIT / CUIL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "20%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Contacto
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "15%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Ubicación
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#5b5b5f",
                      color: "white",
                      width: "10%",
                      padding: "0 16px",
                      height: "45px !important",
                      lineHeight: "45px",
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProveedores.length > 0 ? (
                  paginatedProveedores.map((prov) => (
                    <TableRow
                      key={prov.id}
                      hover
                      sx={{
                        height: "35px",
                        "&:nth-of-type(even)": {
                          backgroundColor: "#F7F7F7",
                        },
                        "&:hover": {
                          backgroundColor: "#E3EAFD",
                          transition: "background 0.3s ease",
                        },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Chip
                          label={prov.id}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            minWidth: 40,
                            justifyContent: "center",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <PersonIcon
                            fontSize="small"
                            sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {prov.descripcion || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        {prov.apellido || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <BadgeIcon
                            fontSize="small"
                            sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                          />
                          <Typography variant="body2">
                            {prov.dniCuitCuil || "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {prov.email && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <EmailIcon
                                fontSize="small"
                                sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                              />
                              <Typography variant="body2" noWrap>
                                {prov.email}
                              </Typography>
                            </Box>
                          )}
                          {prov.telefono && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <PhoneIcon
                                fontSize="small"
                                sx={{ color: "rgb(244, 147, 14)", mr: 1 }}
                              />
                              <Typography variant="body2">
                                {prov.telefono}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip
                          title={`${prov.direccion || ""} ${
                            prov.localidad || ""
                          } ${prov.provincia || ""}`}
                          arrow
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {prov.localidad
                              ? `${prov.localidad}, ${prov.provincia || ""}`
                              : "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          padding: "0 16px",
                          borderBottom: "1px solid #ccc",
                          height: "35px",
                        }}
                      >
                        <Tooltip title="Editar proveedor">
                          <IconButton
                            onClick={() => handleOpenEditModal(prov)}
                            color="primary"
                            size="small"
                            sx={{
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.2
                                ),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      {loading ? (
                        <Typography color="text.secondary">
                          Cargando proveedores...
                        </Typography>
                      ) : searchText ? (
                        <Typography color="text.secondary">
                          No se encontraron proveedores con ese criterio de
                          búsqueda
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">
                          No hay proveedores registrados
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredProveedores.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          />
        </Box>
      </Card>

      {/* Modal de Crear / Editar Proveedor */}
      {openModal && (
        <ModalCrearActualizarEntidad
          open={openModal}
          handleClose={handleCloseModal}
          entidad={selectedProveedor}
          idTipoEntidad={2}
        />
      )}
    </Box>
  );
};

export default TablaEntidadProveedor;
